import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoginRequest, LoginResponse, RegisterRequest, RefreshTokenRequest } from '../models/auth.model';
import { LocalStorageService } from './local-storage.service';
import { readTenantIdFromJwt } from '../utils/jwt.util';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/api/v1/auth`;
  private currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private tokenStorage: LocalStorageService,
    private router: Router
  ) {
    const user = this.tokenStorage.getUser();
    if (user) {
      this.currentUserSubject.next(user);
    }
  }

  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, loginRequest).pipe(
      tap(response => {
        this.tokenStorage.saveToken(response.token);
        this.tokenStorage.saveRole(response.role);
        if (response.refreshToken) {
          this.tokenStorage.saveRefreshToken(response.refreshToken);
        }
        this.persistTenantIdFromAuthResponse(response);
        // Save user info
        const user = {
          username: loginRequest.username,
          role: response.role,
          tenantId: this.tokenStorage.getTenantId() ?? undefined
        };
        this.tokenStorage.saveUser(user);
        this.currentUserSubject.next(user);
      })
    );
  }

  register(registerRequest: RegisterRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, registerRequest);
  }

  refreshToken(refreshToken: string): Observable<LoginResponse> {
    const request: RefreshTokenRequest = { refreshToken };
    return this.http.post<LoginResponse>(`${this.apiUrl}/refresh`, request).pipe(
      tap(response => {
        this.tokenStorage.saveToken(response.token);
        if (response.refreshToken) {
          this.tokenStorage.saveRefreshToken(response.refreshToken);
        }
        this.persistTenantIdFromAuthResponse(response);
      })
    );
  }

  logout(): void {
    const refreshToken = this.tokenStorage.getRefreshToken();
    if (refreshToken) {
      this.http.post(`${this.apiUrl}/logout`, { refreshToken }).subscribe();
    }
    this.tokenStorage.signOut();
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    return this.tokenStorage.isLoggedIn();
  }

  getCurrentUser(): any {
    return this.currentUserSubject.value;
  }

  getRole(): string | null {
    return this.tokenStorage.getRole();
  }

  /**
   * Prefer `tenantId` from login/refresh JSON; fallback to JWT payload if omitted (e.g. older APIs).
   * Tenant is not sent as `X-Tenant-Id` — the server reads it from the access token.
   */
  private persistTenantIdFromAuthResponse(response: LoginResponse): void {
    let id: number | null =
      typeof response.tenantId === 'number' && !Number.isNaN(response.tenantId) ? response.tenantId : null;
    if (id == null) {
      id = readTenantIdFromJwt(response.token);
    }
    if (id != null) {
      this.tokenStorage.saveTenantId(id);
    }
  }
}
